import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useEmployes } from "@/pages/agence/employes/EmployesProvider";

interface OwnerPermissionGuardProps {
  /** Permission fine requise pour afficher le module (ex. « immeubles.voir »). */
  permission: string;
  children: ReactNode;
}

/**
 * Garde de permission au niveau des ROUTES de l'espace Propriétaire.
 *
 * Le menu était déjà filtré par `can()`, mais un employé (gestionnaire ou
 * autre) pouvait encore ouvrir un module réservé par URL directe — y compris
 * des pages réservées au Propriétaire (Employés, Paramètres, Abonnement).
 * Ce garde bloque l'accès lorsqu'une permission fine est nécessaire et absente.
 *
 * - Propriétaire (DG) : aucun employé « prévisualisé » → `can()` renvoie
 *   toujours vrai, l'accès complet est donc conservé.
 * - Super Admin : n'utilise pas ce garde (autre arbre de routes).
 * Aucun deuxième système de permissions : on réutilise le catalogue existant.
 */
export default function OwnerPermissionGuard({ permission, children }: OwnerPermissionGuardProps) {
  const { can, currentEmploye } = useEmployes();

  if (can(permission)) {
    return <>{children}</>;
  }

  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-background-200 bg-background-50 px-6 py-14 text-center">
      <span className="flex h-14 w-14 items-center justify-center rounded-full bg-accent-100 text-accent-700">
        <i className="ri-lock-2-line text-2xl" aria-hidden="true" />
      </span>
      <h2 className="mt-4 font-heading text-lg font-semibold text-foreground-950 md:text-xl">Accès non autorisé</h2>
      <p className="mt-2 max-w-md text-sm leading-relaxed text-foreground-600">
        {currentEmploye
          ? "Votre profil ne dispose pas de la permission nécessaire pour accéder à ce module. Contactez le propriétaire pour l'obtenir."
          : "Vous n'avez pas accès à ce module."}
      </p>
      <Link
        to="/proprietaire"
        className="mt-5 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
      >
        <i className="ri-arrow-left-line text-base" aria-hidden="true" />
        Retour au tableau de bord
      </Link>
    </div>
  );
}